import { useCallback } from 'react';
import { useLocale } from '@hooks/useLocale';
import { useCartUI } from '@context/CartContext';
import type { Cart, ItemBody } from '../../types/cart';
import { HookError } from '../../errorHandlers';

export const addItemFetcher = async (
  item: ItemBody,
  locale: string
): Promise<Cart> => {
  if (
    item.quantity !== undefined &&
    (!Number.isInteger(item.quantity) || item.quantity < 1)
  ) {
    throw new HookError(
      'The item quantity has to be a valid integer greater than 0'
    );
  }

  const addItemResponse = await fetch('/api/cart', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': locale,
    },
    body: JSON.stringify({ item }),
  });

  const { data, errors } = await addItemResponse.json();

  // The route falls back to `res.status(500).json({ data: null, errors })`
  // for any upstream failure it has no specific 422 handler for — without
  // this check a null cart silently reads as "nothing went wrong", and
  // callers (Quick Add, AddToCartCTA) would treat the add as successful.
  if (!addItemResponse.ok || !data) {
    throw new HookError(errors?.[0]?.message ?? 'Failed to add item to cart');
  }

  return data;
};

export type OnAddItemCallback = ({
  data,
  item,
}: {
  data: Cart;
  item?: ItemBody;
}) => void;

const useAddItem = (onAddItem: OnAddItemCallback) => {
  const { updateCart } = useCartUI();
  const locale = useLocale();
  if (!locale)
    throw new HookError('Locale must be specified to add item to cart');

  return useCallback(
    async (input: ItemBody) => {
      const data = await addItemFetcher(input, locale);
      updateCart(data);
      try {
        onAddItem?.({ data, item: input });
      } catch (error) {
        console.log(error);
      }
    },
    [locale, updateCart, onAddItem]
  );
};

export default useAddItem;
